import Box from '@mui/material/Box/Box'
import Stack from '@mui/material/Stack/Stack'
import Typography from '@mui/material/Typography/Typography'
import { LocalizationProvider, PickersDayProps, StaticDatePicker } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import isSameDay from 'date-fns/esm/isSameDay'
import format from 'date-fns/format'
import startOfDay from 'date-fns/startOfDay'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import Divider from '@mui/material/Divider/Divider'
import { useBookingContext } from '@providers/booking/booking_context'
import { useLocationContext } from '@providers/location'
import { buildPetGroups } from '@shared/booking_groups'
import { getDateRange } from '@shared/dates'
import { buildErrorMessage } from '@shared/error'
import { i18WithDefault as t } from '@shared/locale'
import { isWaitlistEnabled } from '@shared/locations'
import { maximumSelectableDate } from '@shared/service_types'
import { DayCalendarSkeleton } from 'components-ui/src/date_picker_skeleton/date_picker_skeleton'
import { isEmpty } from 'components-ui/src/shared/common'
import { waitlistEligible } from 'components-ui/src/shared/offers'
import { ClientBookingSearchService } from 'service-api/src/services/client_booking_search.service'
import { captureError } from 'service-api/src/shared/monitoring'
import { AvailabilityGroupClientDto } from 'service-api/src/types/api'
import { TBookingSearchOptions } from 'service-api/src/types/general'
import { CustomPickersDay } from './custom_picker_day'
import { isDataInCache } from './helper'
import { WithTooltip } from './with_tooltip'

export const DatePicker = () => {
  const bookingCtx = useBookingContext()
  const { location } = useLocationContext()
  const { state } = bookingCtx
  const serviceTypeName = state.serviceTypeName
  const { accountName: locationName = '' } = useParams()
  const endDate = maximumSelectableDate(state.startDate!, state)
  const isFetching = useRef<boolean>(false)
  const [isLoading, setIsLoading] = useState(true)
  const [requestDates, setRequestDates] = useState<Date[]>(() => {
    return getDateRange(state.startDate!, state.startDate!, endDate)
  })
  const hasWaitlist = (location?.subscriberGroup?.waitlistEnabled ?? false) && isWaitlistEnabled(state)
  const waitlistSelected = state.cart.bookWaitlist

  const searchParams = useMemo(() => {
    return {
      petGroups: buildPetGroups(state, false),
      offersType: 'PRIMARY',
      offerName: state.cart.groups[0].offerSelected!.offer!.name,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cart.groups[0].offerSelected!.offer!.name])

  useEffect(() => {
    if (isFetching.current) {
      return
    }

    if (isDataInCache(requestDates[0], requestDates[1], state)) {
      setIsLoading(false)
      return
    }

    isFetching.current = true
    ;(async () => {
      const params = {
        ...searchParams,
        startDate: format(requestDates[0], 'yyyy-MM-dd'),
        endDate: format(requestDates[1], 'yyyy-MM-dd'),
      } as unknown as TBookingSearchOptions
      try {
        const response = await ClientBookingSearchService.searchByDays(locationName, serviceTypeName, params)
        bookingCtx.dispatch({
          type: 'SET_OFFER_AVAILABILITY',
          payload: {
            availability: response.results[0]?.availabilityGroupsByDays,
            startDate: requestDates[0],
          },
        })
        bookingCtx.dispatch({ type: 'LOAD_OFFERS_SEARCH_RESULTS', payload: { value: response.results || [] } })

        setIsLoading(false)
        isFetching.current = false
      } catch (error) {
        enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
        captureError(error as Error)
        isFetching.current = false
        setIsLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationName, requestDates, serviceTypeName])

  const hasAvailabilityReason = useCallback(
    (availability: AvailabilityGroupClientDto) => {
      return hasWaitlist && waitlistEligible(availability.availabilities)
    },
    [hasWaitlist]
  )

  const isDateAvailable = useCallback(
    (day: Date) => {
      const dateSelected = state.cart.groups[0].daycareSelected?.[0]?.date
      // if we already selected a date for waitlist, we don't want to show any other available date
      if (waitlistSelected) {
        // except the selected day
        return isSameDay(day, dateSelected || 0)
      }

      const idx = state.data.offerAvailability?.findIndex(({ id }) => id === day.getTime()) ?? -1
      if (idx < 0) {
        return false
      }
      const availability = state.data.offerAvailability?.[idx]?.availability?.availabilities?.[0]
      if (!availability) {
        return false
      }

      if (availability.available) {
        return true
      }

      if (hasAvailabilityReason(state.data.offerAvailability![idx].availability)) {
        return isEmpty(dateSelected)
      }

      return availability.available
    },
    [hasAvailabilityReason, state.cart.groups, state.data.offerAvailability, waitlistSelected]
  )

  const shouldDisableDate = useCallback((day: Date) => !isDateAvailable(day), [isDateAvailable])

  const onChangeDateValue = (selectedDate: Date | null) => {
    if (!selectedDate) {
      return
    }

    const date = startOfDay(selectedDate)
    const dateTime = date.getTime()
    const idx = state.data.offerAvailability?.findIndex(({ id }) => id === dateTime) ?? -1
    if (idx < 0) {
      return
    }

    bookingCtx.dispatch({
      type: 'SET_DAYCARE_DATES',
      payload: { value: date, availability: state.data.offerAvailability?.[idx] },
    })
  }

  const dates = useMemo(
    () => state.cart.groups[0].daycareSelected?.map(({ date }) => date) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.cart.groups[0].daycareSelected]
  )

  const renderPickerDay = useCallback(
    (date: Date, _selectedDates: Date[], pickersDayProps: PickersDayProps<Date>) => {
      const isDateSelected = (dates.findIndex((dc) => dc.getTime() === date.getTime()) ?? -1) >= 0
      const idx = state.data.offerAvailability?.findIndex(({ id }) => id === date.getTime()) ?? -1
      let forWaitlist = false

      if (hasWaitlist) {
        const availability = state.data.offerAvailability?.[idx]?.availability
        if (availability) {
          if (hasAvailabilityReason(availability)) {
            forWaitlist = !isDateSelected || !waitlistSelected
          }
        }
      }

      const hasTooltip = hasWaitlist && forWaitlist && shouldDisableDate(date)

      return (
        <WithTooltip key={pickersDayProps.key} hasTooltip={hasTooltip} title={t('datesSelection.waitlist.tooltip')}>
          <CustomPickersDay {...pickersDayProps} selected={isDateSelected} forWaitlist={forWaitlist} />
        </WithTooltip>
      )
    },
    [dates, hasAvailabilityReason, hasWaitlist, shouldDisableDate, state.data.offerAvailability, waitlistSelected]
  )

  const handleMonthChange = (date: Date) => {
    const [fromDate, toDate] = getDateRange(date, state.startDate!, endDate)
    setRequestDates([fromDate, toDate])
    if (isDataInCache(fromDate, toDate, state)) {
      return
    }
    setIsLoading(true)
  }

  const monthButtons = isLoading
    ? {
        LeftArrowButton: () => <></>,
        RightArrowButton: () => <></>,
      }
    : {}

  return (
    <Stack direction="column" gap={2}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          showToolbar={false}
          value={dates}
          minDate={state.startDate}
          maxDate={endDate}
          shouldDisableDate={shouldDisableDate}
          onChange={onChangeDateValue}
          renderDay={renderPickerDay}
          renderInput={(_props) => <></>}
          views={['day']}
          loading={isLoading}
          renderLoading={() => <DayCalendarSkeleton />}
          onMonthChange={handleMonthChange}
          components={monthButtons}
        />
      </LocalizationProvider>
      {hasWaitlist && (
        <>
          <Divider />
          <Stack direction="row" gap={1} alignItems="center" pl={4}>
            <Box
              sx={{
                width: 16,
                height: 16,
                border: '1px dashed black',
                borderRadius: '50%',
                borderColor: 'text.secondary',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {t('addonDatePicker.waitlistOnly')}
            </Typography>
          </Stack>
        </>
      )}
    </Stack>
  )
}
